import { Clock, ListChecks, Timer } from 'lucide-react';
import { QuizPlayerWelcomeProps } from '../types';
import { Button as BaseButton, ButtonProps } from '@/components/button';
import { useTranslation } from '@/hooks/use-translation';
import { QuizPlayerScreen } from './quiz-player-screen';
import { cn } from '@/utils';

type QuizPlayerWelcomeScreenProps = React.ComponentProps<'div'> &
  QuizPlayerWelcomeProps & {
    slots?: { Button?: React.ElementType<ButtonProps> };
    slotProps?: { button?: ButtonProps };
  };

export function QuizPlayerWelcomeScreen({
  title,
  description,
  questions = [],
  onStart = () => {},
  onCancel = () => {},
  slots = {},
  slotProps,
  details: customDetails = [],
}: QuizPlayerWelcomeScreenProps) {
  const { t } = useTranslation();
  const { Button = BaseButton } = slots;
  const totalTimeLimit = questions.reduce(
    (acc, q) => acc + (q.timeLimit || 0),
    0
  );
  const avgTimePerQuestion = Math.round(totalTimeLimit / questions.length);

  const details: { icon: React.ReactNode; label: React.ReactNode }[] = [
    {
      icon: <ListChecks size={20} />,
      label: t('questions', {
        fallback: '{value} Domande',
        args: { value: questions.length },
      }),
    },
  ];

  if (avgTimePerQuestion) {
    details.push({
      icon: <Clock size={20} />,
      label: t('quizTimeLimit', {
        fallback: 'Circa {value} secondi per domanda.',
        args: { value: avgTimePerQuestion },
      }),
    });
  }

  if (totalTimeLimit) {
    details.push({
      icon: <Timer size={20} />,
      label: t('quizTimeLimit', {
        fallback: 'Tempo totale: {value} minuti.',
        args: { value: totalTimeLimit / 60 },
      }),
    });
  }

  return (
    <QuizPlayerScreen variant="welcome">
      <div className="mm:text-3xl mm:font-bold mm:mb-4 mm:font-brand">
        {title}
      </div>
      <p className="mm:mb-8">{description}</p>

      <div className="mm:space-y-4 mm:mb-8">
        {details.concat(customDetails).map((item, i) => (
          <div
            key={i}
            className="mm:flex mm:items-center mm:gap-3 mm:text-muted-foreground"
          >
            {item.icon}
            <span>{item.label}</span>
          </div>
        ))}
      </div>

      <div className="mm:flex mm:gap-2 mm:items-center">
        <Button
          {...slotProps?.button}
          size="lg"
          variant="brand"
          onClick={onStart}
          className={cn('mm:capitalize', slotProps?.button?.className)}
        >
          {t('start', 'Inizia')}
        </Button>
        <Button
          {...slotProps?.button}
          variant="link"
          className={cn(
            'mm:capitalize mm:text-muted-foreground',
            slotProps?.button?.className
          )}
          onClick={onCancel}
        >
          {t('cancel', 'Annulla')}
        </Button>
      </div>
    </QuizPlayerScreen>
  );
}
